import {Box, Divider, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import './DocumentSelection.scss';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import SharedSavedSearchTree from './shared-saved-search-tree/SharedSavedSearchTree';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {ReactComponent as ProcessingIcon} from '../../../../../assets/images/icon-processing.svg';
import {ReactComponent as ProcessingIconDisabled} from '../../../../../assets/images/processingIconDisabled.svg';
import ArrBanner from '../../../../common/arr-banner/ArrBanner';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {useSelector, useDispatch} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {RootState, AppDispatch} from '../../../../../redux/store';
import {
  setCreateCustomIndex,
  setSecurityGroupCount,
  setSelectedDocument,
} from '../../../../../redux/slice/createCustomIndexSlice';
import classNames from 'classnames';
import {useSavedSearchList} from '../../../../../hooks/saved-search/useSavedSearch';
import {formatUtcDateTime} from '../CreateCustomIndexUtils';
import {useRunBackgroundSearchQueue} from '../../../../../hooks/search/useSearch';
import {IRunBackgroundSearchQueueRequest} from '../../../../../api/types/searchType.type';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ISecurityGroupDocument} from '../../../../../api/types/savedsearch.types';
import TruncatedTooltip from '../../../../document-viewer/doc-list/truncated-tooltip/TruncatedTooltip';
import {useSecurityGroupIdCount} from '../../../../../hooks/ingestions/useIngestions';
import {
  NestedSearchExecutionContext,
  resolveNestedSearchFlow,
  resolveSavedSearchNestedParams,
} from '../../../../session-search/utilities/nestedSearchUtils';
import {useGetNestedSearches} from '../../../../../hooks/session-search/useSessionSearch';

const DOC_COUNT_POLL_INTERVAL_MS = 5_000;
const DOC_COUNT_POLL_TIMEOUT_MS = 15_000;

const isClearedDocCount = (result?: string | null) =>
  result == null || result === '' || result === '0';

const DocumentSelectionCustomIndex: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const {isReadOnlyView, createCustomIndex} = useSelector(
    (state: RootState) => state.indexManagement
  );
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedItemData, setSelectedItemData] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const nestedSearchContextRef = useRef<NestedSearchExecutionContext | null>(
    null
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);
  const [restoredDocCount, setRestoredDocCount] = useState<string | null>(null);
  const prevStatusRef = useRef<string | undefined>(undefined);
  const refreshTimedOutRef = useRef(false);
  const updateSavedSearchListRef = useRef<() => void>(() => undefined);
  const pollIntervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const refreshBaselineRef = useRef<{
    result?: string;
    lastCompleteTime?: string;
  } | null>(null);

  const payload = {
    searchID: Number(selectedItemData?.id),
    searchGroupId: Number(parseInt(selectedItemData?.parent ?? '', 10)),
    sortBy: '',
    sortingOrder: '',
    pageNumber: 1,
    pageSize: 10,
  };

  const {
    data: savedSearchDetail,
    isLoading: isSavedSearchListLoading,
    refetch: updateSavedSearchList,
    isRefetching: isSavedSearchRefetching,
  } = useSavedSearchList(payload, !!selectedItem, false);
  updateSavedSearchListRef.current = updateSavedSearchList;

  const {mutate: runBackgroundSearchQueue} = useRunBackgroundSearchQueue();

  const selectedSearchId = Number(selectedItem) || 0;
  const securityGroupIds = createCustomIndex?.securityGroupIds;
  const securityGroupCountPayload = useMemo(
    () => ({
      securityGroupIds: securityGroupIds ?? [],
      searchId: selectedSearchId,
    }),
    [securityGroupIds, selectedSearchId]
  );

  const {data: securityGroupDocCount, refetch: refetchSecurityGroupCount} =
    useSecurityGroupIdCount(securityGroupCountPayload);

  const {mutate: getNestedSearches} = useGetNestedSearches();

  const displayedDocCount =
    restoredDocCount !== null
      ? restoredDocCount
      : (savedSearchDetail?.data[0]?.result ?? '');
  const isDisplayedCountEmpty = isClearedDocCount(displayedDocCount);
  const refreshDocLoad = isRefreshing;

  const clearDocCountTimers = () => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const stopRefreshWait = (timedOut: boolean) => {
    clearDocCountTimers();
    void queryClient.cancelQueries({queryKey: ['reportLanding']});
    setIsRefreshing(false);
    if (timedOut) {
      refreshTimedOutRef.current = true;
      setRefreshTimedOut(true);
      setRestoredDocCount(refreshBaselineRef.current?.result ?? '');
      return;
    }
    refreshTimedOutRef.current = false;
    setRefreshTimedOut(false);
  };
  const stopRefreshWaitRef = useRef(stopRefreshWait);
  stopRefreshWaitRef.current = stopRefreshWait;

  useEffect(() => {
    if (securityGroupDocCount?.securityGroupUserCounts) {
      dispatch(
        setSecurityGroupCount(
          securityGroupDocCount?.securityGroupUserCounts ?? []
        )
      );
    }
  }, [securityGroupDocCount?.securityGroupUserCounts]);

  useEffect(() => {
    if (refreshTimedOut || isRefreshing) {
      return;
    }

    if (savedSearchDetail?.totalRecords !== 0) {
      const documentSelected = {
        source: savedSearchDetail?.data[0]?.searchName ?? '',
        documents: savedSearchDetail?.data[0]?.result
          ? savedSearchDetail?.data[0]?.result
          : 0,
        savedSearchSecurityGroup:
          savedSearchDetail?.data[0]?.securityGroupsDocumentCount ?? [],
      };
      dispatch(setSelectedDocument(documentSelected));
    }
  }, [savedSearchDetail, refreshTimedOut, isRefreshing]);

  const settledSavedSearchIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isSavedSearchListLoading && !isSavedSearchRefetching) {
      settledSavedSearchIdRef.current = selectedSearchId;
      return;
    }

    const isPollForCurrentSearch =
      isSavedSearchRefetching &&
      settledSavedSearchIdRef.current === selectedSearchId;

    if (
      refreshTimedOutRef.current ||
      !isPollForCurrentSearch ||
      (securityGroupIds?.length ?? 0) === 0
    ) {
      return;
    }

    void refetchSecurityGroupCount();
  }, [
    isSavedSearchListLoading,
    isSavedSearchRefetching,
    selectedSearchId,
    securityGroupIds,
    refetchSecurityGroupCount,
  ]);

  useEffect(() => {
    setIsRefreshing(false);
    setRefreshTimedOut(false);
    refreshTimedOutRef.current = false;
    setRestoredDocCount(null);
    refreshBaselineRef.current = null;
    prevStatusRef.current = undefined;
    clearDocCountTimers();
  }, [selectedSearchId]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        window.clearInterval(pollIntervalRef.current);
      }
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (refreshTimedOutRef.current) {
      return;
    }

    const current = savedSearchDetail?.data[0];
    if (!current) {
      return;
    }

    const status = current.status;
    const wasInProgress = prevStatusRef.current === 'INPROGRESS';
    prevStatusRef.current = status;

    const baseline = refreshBaselineRef.current;
    if (!baseline) {
      return;
    }

    const searchFinished = wasInProgress && !!status && status !== 'INPROGRESS';
    const resultChanged = current.result !== baseline.result;
    const timeChanged = current.lastCompleteTime !== baseline.lastCompleteTime;
    const isTransientClearedCount =
      isClearedDocCount(current.result) && !timeChanged;
    const settledAfterRefresh =
      status !== 'INPROGRESS' &&
      (resultChanged || timeChanged) &&
      !isTransientClearedCount;

    if (searchFinished || settledAfterRefresh) {
      stopRefreshWait(false);
      setRestoredDocCount(null);
      refreshBaselineRef.current = null;
    }
  }, [
    savedSearchDetail?.data[0]?.status,
    savedSearchDetail?.data[0]?.result,
    savedSearchDetail?.data[0]?.lastCompleteTime,
    selectedSearchId,
  ]);

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const searchText = searchInput?.trim();
      setSearchKeyword(searchText ?? '');
    }
  };

  const getSelectedTreeItem = (itemId: string) => {
    setSelectedItem(itemId);
    const parsedSavedSearchId = Number(itemId);
    dispatch(
      setCreateCustomIndex({
        savedSearchId:
          Number.isNaN(parsedSavedSearchId) || itemId === ''
            ? null
            : parsedSavedSearchId,
      })
    );
  };

  const getSelectedTreeItemData = (
    item: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    setSelectedItemData(item);
  };

  const handleRefreshCount = () => {
    if (refreshDocLoad) {
      return;
    }

    const nestedSearchParams = resolveSavedSearchNestedParams({
      searchId: Number(selectedItemData?.id),
      searchGroupIds: [Number(parseInt(selectedItemData?.parent ?? '', 10))],
    });

    if (!nestedSearchParams) {
      return;
    }

    setIsRefreshing(true);
    setRefreshTimedOut(false);
    refreshTimedOutRef.current = false;
    setRestoredDocCount(null);
    refreshBaselineRef.current = {
      result: savedSearchDetail?.data[0]?.result,
      lastCompleteTime: savedSearchDetail?.data[0]?.lastCompleteTime,
    };

    clearDocCountTimers();
    timeoutRef.current = window.setTimeout(() => {
      stopRefreshWaitRef.current(true);
    }, DOC_COUNT_POLL_TIMEOUT_MS);

    getNestedSearches(nestedSearchParams, {
      onSuccess: (result) => {
        const flow = resolveNestedSearchFlow(result);

        if (flow.outcome === 'error') {
          nestedSearchContextRef.current = null;
          clearDocCountTimers();
          setIsRefreshing(false);
          return;
        }

        nestedSearchContextRef.current = flow.context;

        if (flow.outcome === 'proceed') {
          handleProceed();
          return;
        }

        clearDocCountTimers();
        setIsRefreshing(false);
      },
      onError: () => {
        clearDocCountTimers();
        setIsRefreshing(false);
      },
    });
  };

  const handleProceed = () => {
    const payload: IRunBackgroundSearchQueueRequest = {
      searchsecuritygroupid: selectedItemData?.securitygroupsearchid || 0,
      searchID: Number(selectedItemData?.id),
      searchGroupId: Number(parseInt(selectedItemData?.parent ?? '', 10)),
      ...nestedSearchContextRef.current,
    };

    runBackgroundSearchQueue(payload, {
      onSuccess: () => {
        if (refreshTimedOutRef.current) {
          return;
        }

        void updateSavedSearchListRef.current();

        if (pollIntervalRef.current !== null) {
          window.clearInterval(pollIntervalRef.current);
        }

        pollIntervalRef.current = window.setInterval(() => {
          if (refreshTimedOutRef.current) {
            return;
          }
          void updateSavedSearchListRef.current();
        }, DOC_COUNT_POLL_INTERVAL_MS);
      },
      onError: () => {
        clearDocCountTimers();
        setIsRefreshing(false);
      },
    });
  };

  return (
    <Box className="doc-custom-index-wrapper">
      <Box className="doc-index-header">
        <Typography
          className="typo-text subhead2_size doc-index-title-text"
          data-testid={`document-selection-doc-selection-title`}
          tabIndex={0}
        >
          {t('ingestions.custom-index.stepper-tab-2.doc-selection-title')}
        </Typography>
      </Box>
      <Box className="dox-index-content">
        <Grid
          container
          spacing={6}
          rowSpacing={12}
          className="dox-index-content-container"
        >
          <Grid
            size={{xs: 12, sm: 6}}
            className="saved-search-dropdown-content"
          >
            <Box className="select-saved-search-title">
              <Typography
                className="typo-text subhead3_size"
                tabIndex={0}
                data-testid={`document-selection-select-saved-search-title`}
              >
                {t(
                  'ingestions.custom-index.stepper-tab-2.select-saved-search-title'
                )}
              </Typography>
              <Typography
                className="typo-text body3_size select-sub-header"
                tabIndex={0}
                data-testid={`document-selection-select-sub-header`}
              >
                {t('ingestions.custom-index.stepper-tab-2.select-sub-header')}
              </Typography>
            </Box>
            <Box
              className={classNames(
                'search-box',
                isReadOnlyView && 'search-box-inactive'
              )}
            >
              <ArrTextField
                state={isReadOnlyView ? 'inactive' : 'enabled'}
                sizeVariant="medium"
                tabIndex={isReadOnlyView ? 0 : -1}
                className="search-input"
                placeholder={t(
                  `ingestions.custom-index.stepper-tab-2.search-placeholder`
                )}
                aria-label={t(
                  'ingestions.custom-index.stepper-tab-2.search-placeholder'
                )}
                dataTestId="doc-selection-search-input"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (e.target.value.trim() === '') {
                    setSearchKeyword('');
                  }
                }}
                onKeyDown={handleSearchKeyEnter}
                onClear={() => {
                  setSearchInput('');
                  setSearchKeyword('');
                }}
                type="search"
                slotProps={{
                  inputLabel: {shrink: false},
                }}
              />
            </Box>
            <Box>
              <SharedSavedSearchTree
                getSelectedTreeItem={getSelectedTreeItem}
                getSelectedTreeItemData={getSelectedTreeItemData}
                searchKeyword={searchKeyword}
              />
            </Box>
          </Grid>

          <Grid
            size={{xs: 12, sm: 6}}
            className="selected-search-details-content"
          >
            <Box
              sx={{
                visibility:
                  (selectedItem || createCustomIndex?.savedSearchId) &&
                  !isSavedSearchListLoading
                    ? 'visible'
                    : 'hidden',
              }}
              className="search-details-box"
            >
              <Box className="details-header-box">
                <Box sx={{width: '100%', minWidth: 0}}>
                  <TruncatedTooltip
                    content={savedSearchDetail?.data[0]?.searchName}
                    className="typo-text subhead3_size header-detail-title"
                    dataTestId={`document-selection-search-detail-title`}
                  />
                </Box>
                <Typography
                  tabIndex={0}
                  className="typo-text body4_size details-sub-headings"
                  data-testid={`document-selection-detail-id`}
                >
                  {t('ingestions.custom-index.stepper-tab-2.detail-id')}{' '}
                  {savedSearchDetail?.data[0]?.searchID}
                </Typography>
                <Divider className="details-text-divider" />
                <Typography
                  className="typo-text body4_size details-sub-headings"
                  data-testid={`document-selection-last-submit-time`}
                  tabIndex={0}
                >
                  {t('ingestions.custom-index.stepper-tab-2.last-submit-time')}{' '}
                  {formatUtcDateTime(
                    savedSearchDetail?.data[0]?.submitTime ?? ''
                  )}
                </Typography>
                <Typography
                  className="typo-text body4_size details-sub-headings"
                  data-testid={`document-selection-last-complete-time`}
                  tabIndex={0}
                >
                  {t(
                    'ingestions.custom-index.stepper-tab-2.last-complete-time'
                  )}{' '}
                  {formatUtcDateTime(
                    savedSearchDetail?.data[0]?.lastCompleteTime ?? ''
                  )}
                </Typography>
              </Box>

              <Box className="details-doc-count-box">
                <Box className="doc-refresh-count">
                  <Box className="doc-last-count">
                    <Typography
                      tabIndex={0}
                      className="typo-text body3_size details-sub-headings"
                      data-testid={`document-selection-last-doc-count`}
                    >
                      {t(
                        'ingestions.custom-index.stepper-tab-2.last-doc-count'
                      )}{' '}
                      {refreshDocLoad ? (
                        <Box component="span" className="loader-container">
                          <ArrLoadingSpinner size={18} />
                        </Box>
                      ) : (
                        <Typography
                          className={`typo-text body3_size loader-container ${isDisplayedCountEmpty ? 'doc-count-empty' : 'doc-count-nonempty'}`}
                          data-testid="document-selection-count-empty"
                        >
                          {displayedDocCount != '' ? displayedDocCount : 0}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <span
                    tabIndex={isReadOnlyView ? 0 : -1}
                    aria-label="inactive-refresh-doc-button"
                    data-testid={`document-selection-refresh-doc-count`}
                  >
                    <ArrButton
                      variant="text"
                      size="small"
                      tabIndex={0}
                      onClick={handleRefreshCount}
                      isAriaDisabled={isReadOnlyView || refreshDocLoad}
                      aria-label={t(
                        'ingestions.custom-index.stepper-tab-2.refresh-doc-count'
                      )}
                      dataTestId="refresh-doc-count-button"
                      startIcon={
                        isReadOnlyView ? (
                          <ProcessingIconDisabled />
                        ) : (
                          <ProcessingIcon />
                        )
                      }
                    >
                      {t(
                        'ingestions.custom-index.stepper-tab-2.refresh-doc-count'
                      )}
                    </ArrButton>
                  </span>
                  {isDisplayedCountEmpty && !refreshDocLoad && (
                    <ArrBanner
                      type="contextual"
                      variant="warning"
                      className="banner-text"
                      bannerContent={t(
                        'ingestions.custom-index.stepper-tab-2.banner-info'
                      )}
                      startIcon={<InfoIcon />}
                      dataTestId="doc-selection-banner"
                    />
                  )}
                </Box>
                {createCustomIndex?.securityGroupIds?.length !== 0 &&
                  securityGroupDocCount?.securityGroupUserCounts?.length !==
                    0 && <Divider className="details-text-divider" />}
                <Box
                  className="doc-security-group"
                  data-testid="doc-selection-sec-groups"
                >
                  {securityGroupDocCount?.securityGroupUserCounts?.map(
                    (group: ISecurityGroupDocument, index: number) => (
                      <Typography
                        key={group?.securityGroupName}
                        tabIndex={0}
                        className="typo-text body4_size details-sub-headings"
                        data-testid={`document-selection-security-group-name-${index}`}
                      >
                        {`${group?.securityGroupName}: `}
                        <Typography
                          className={`typo-text label3_size loader-container ${refreshDocLoad ? 'refresh-count-empty' : 'doc-count-nonempty'}`}
                          data-testid={`document-selection-security-group-count-${index}`}
                        >
                          {refreshDocLoad ? '---' : group?.documentCount}
                        </Typography>
                      </Typography>
                    )
                  )}
                </Box>
              </Box>

              <Box
                className="details-index-box"
                data-testid="doc-selection-index-details"
              >
                <Typography
                  className="typo-text body3_size details-sub-headings"
                  tabIndex={0}
                  data-testid={`document-selection-index`}
                >
                  {t('ingestions.custom-index.stepper-tab-2.index')}{' '}
                  <Typography
                    className="typo-text body3_size loader-container doc-count-nonempty"
                    data-testid="document-selection-index-name"
                  >
                    {savedSearchDetail?.data[0]?.indexName}
                  </Typography>
                </Typography>
              </Box>

              <Box className="details-syntax-box">
                <Typography
                  className="typo-text body3_size details-sub-headings"
                  data-testid={`document-selection-syntax-title`}
                >
                  {t('ingestions.custom-index.stepper-tab-2.syntax')}
                </Typography>
                <Typography
                  className="typo-text subhead5_size syntax-content"
                  tabIndex={0}
                  data-testid={`document-selection-syntax`}
                >
                  {savedSearchDetail?.data[0]?.syntax}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {isSavedSearchListLoading && !isRefreshing && !refreshTimedOut && (
        <ArrLoadingSpinner
          show={isSavedSearchListLoading}
          overlay={true}
          isOpen={true}
        />
      )}
    </Box>
  );
};

export default DocumentSelectionCustomIndex;
